import React, { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import Plural from 'components/utils/Formatters/Plural'
import { tierProgressionNotificationMessages } from './LoyaltyTierProgressionNotificationMessages'

const messages = {
  ...tierProgressionNotificationMessages,
  toUnlockSuffix: { id: 'LTPN10UnlkM', defaultMessage: 'to unlock {tierName} benefits.', description: 'Suffix on mobile tier progression notification' },
} as const

const MobileNotificationCard = styled.div`
  background: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${rem(8)};
  box-shadow: 
    0 ${rem(-1)} ${rem(5)} 0 rgba(0, 0, 0, 0.1),
    0 ${rem(-4)} ${rem(14)} 0 rgba(0, 0, 0, 0.05),
    0 ${rem(-8)} ${rem(24)} 0 rgba(0, 0, 0, 0.05),
    0 ${rem(-24)} ${rem(35)} 0 rgba(0, 0, 0, 0.05);
  padding: ${rem(12)} ${rem(12)};
  display: flex;
  align-items: center;
  gap: ${rem(12)};
`

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(5)};
  min-width: 0;
`

const CloseButtonWrapper = styled.div`
  flex-shrink: 0;
  align-self: flex-start;
`

const RequirementsText = styled(BodyText)`
  color: ${props => props.theme.palette.neutral.default.one};
`

const BoldText = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.bold};
`

interface Props {
  nextTier: App.LoyaltyTier
  creditsRemaining: number
  nightsRemaining: number
  userName?: string
  onViewProgress?: () => void
  onClose?: () => void
}

function LoyaltyTierProgressionNotificationMobile({
  nextTier,
  creditsRemaining,
  nightsRemaining,
  userName,
  onViewProgress,
  onClose,
}: Props) {
  const intl = useIntl()
  const tierName = getLoyaltyTierDisplayName(nextTier)

  const hasCredits = creditsRemaining > 0
  const hasNights = nightsRemaining > 0

  const headerText = intl.formatMessage(messages.headerText, {
    userName: userName || 'you',
    tierName,
    tierHighlight: (chunks: React.ReactNode) => chunks,
  })
  const youOnlyNeedText = intl.formatMessage(messages.youOnlyNeed)
  const toUnlockSuffix = intl.formatMessage(messages.toUnlockSuffix, { tierName })
  const statusCreditSingular = intl.formatMessage(messages.statusCreditSingular)
  const statusCreditPlural = intl.formatMessage(messages.statusCreditPlural)
  const nightSingular = intl.formatMessage(messages.nightSingular)
  const nightPlural = intl.formatMessage(messages.nightPlural)
  const andConjunction = intl.formatMessage(messages.andConjunction)
  const viewProgressText = intl.formatMessage(messages.viewProgress)
  const closeAriaLabel = intl.formatMessage(messages.closeAriaLabel)

  const handleViewProgress = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    onViewProgress?.()
  }, [onViewProgress])

  return (
    <MobileNotificationCard>
      <LoyaltyTierIcon tier={nextTier} size={40} />

      <ContentSection>
        <BodyText variant="medium" weight="bold">
          {headerText}
        </BodyText>

        <RequirementsText variant="small">
          {youOnlyNeedText}{' '}
          {hasCredits && <BoldText><Plural singular={statusCreditSingular} plural={statusCreditPlural} count={creditsRemaining} withCount /></BoldText>}
          {hasCredits && hasNights && ` ${andConjunction} `}
          {hasNights && <BoldText><Plural singular={nightSingular} plural={nightPlural} count={nightsRemaining} withCount /></BoldText>}
          {' '}{toUnlockSuffix}
        </RequirementsText>
        <TextLink size="small" weight="regular" onClick={handleViewProgress}>
          {viewProgressText}
        </TextLink>
      </ContentSection>

      <CloseButtonWrapper>
        <IconButton
          kind="tertiary"
          size="small"
          onClick={onClose}
          aria-label={closeAriaLabel}
        >
          <LineTimesIcon colour="neutral-one" />
        </IconButton>
      </CloseButtonWrapper>
    </MobileNotificationCard>
  )
}

export default LoyaltyTierProgressionNotificationMobile
